import { useTranslation } from "react-i18next";
import { CreditCard, Buildings, Wallet, CheckIcon } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

export type PaymentMethodId = "monri" | "bank_transfer" | "company_credit";

export interface CompanyCreditOption {
  companyName: string;
  billingModel: "credit" | "invoice";
  /** null kod modela fakture (nema limita). */
  balance: number | null;
  discountPercent: number;
  cofundingPercent: number;
  /** Dio firme (kod pune pokrivenosti cijela cijena nakon rabata). */
  chargeAmount: number;
  /** Dio zaposlenog, 0 kad firma plaća sve. */
  clientShare: number;
  fullyCovered: boolean;
  /** Kod kredita: ima li dovoljno za dio firme. Kod fakture uvijek true. */
  sufficient: boolean;
}

function fmtKm(n: number): string {
  return `${new Intl.NumberFormat("bs-BA", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n)} KM`;
}

export function PaymentStep({
  selected,
  onSelect,
  companyCredit,
  hideBankTransfer,
}: {
  selected: PaymentMethodId | null;
  onSelect: (method: PaymentMethodId) => void;
  companyCredit?: CompanyCreditOption | null;
  /** Emergency bookings are paid immediately; the server rejects bank transfer. */
  hideBankTransfer?: boolean;
}) {
  const { t } = useTranslation();

  const baseOptions: Array<{
    id: PaymentMethodId;
    icon: React.ComponentType<{ className?: string }>;
    title: string;
    description?: string;
    disabled?: boolean;
    disabledNote?: string;
  }> = [
    {
      id: "monri",
      icon: CreditCard,
      title: t("booking.payCard"),
      description: t("booking.payCardDesc"),
    },
    ...(hideBankTransfer ? [] : [{
      id: "bank_transfer" as const,
      icon: Buildings,
      title: t("booking.payTransfer"),
      description: t("booking.payTransferDesc"),
    }]),
  ];

  // Firma koja sufinansira nema zasebnu opciju: njen dio se primjenjuje sam, a
  // zaposleni bira kako plaća svoj dio (kartica / virman).
  const options = companyCredit?.fullyCovered
    ? [
        {
          id: "company_credit" as const,
          icon: Wallet,
          title: t("booking.payCompany"),
          description: companyCredit.sufficient
            ? companyCredit.billingModel === "invoice"
              ? t("booking.payCompanyInvoice")
              : t("booking.payCompanyAvailable")
            : undefined,
          disabled: !companyCredit.sufficient,
          disabledNote: !companyCredit.sufficient ? t("booking.payCompanyInsufficient") : undefined,
        },
        ...baseOptions,
      ]
    : baseOptions;

  return (
    <div>
      <p className="mb-3 text-sm font-medium text-foreground">
        {t("booking.selectPayment")}
      </p>
      {companyCredit && !companyCredit.fullyCovered && (
        <div data-testid="booking-company-cofunding" className="mb-3 rounded-2xl border border-border/70 bg-muted/30 p-4 text-[13px]">
          {companyCredit.sufficient ? (
            <>
              <p className="font-semibold text-foreground">
                {t("booking.payCompanyCofunding", {
                  companyName: companyCredit.companyName,
                  percent: companyCredit.cofundingPercent,
                  amount: fmtKm(companyCredit.chargeAmount),
                })}
              </p>
              <p className="mt-0.5 text-muted-foreground">
                {t("booking.payCompanyCofundingClient", { amount: fmtKm(companyCredit.clientShare) })}
              </p>
              {/* Rabat firme smanjuje ukupno, pa dio firme + dio zaposlenog ne daju cijenu iz liste. */}
              {companyCredit.discountPercent > 0 && (
                <p className="mt-0.5 text-[11px] text-muted-foreground/80">
                  {t("booking.payCompanyDiscountNote", { companyName: companyCredit.companyName, percent: companyCredit.discountPercent })}
                </p>
              )}
            </>
          ) : (
            <p className="text-amber-700">{t("booking.payCompanyCofundingInsufficient")}</p>
          )}
        </div>
      )}
      <div className="space-y-2">
        {options.map((opt) => {
          const active = selected === opt.id && !opt.disabled;
          return (
            <button
              key={opt.id}
              data-testid={`booking-pay-${opt.id}`}
              disabled={opt.disabled}
              onClick={() => !opt.disabled && onSelect(opt.id)}
              className={cn(
                "flex w-full items-start gap-3 rounded-2xl border p-4 text-left transition-all",
                opt.disabled
                  ? "cursor-not-allowed border-border/40 bg-muted/30 opacity-60"
                  : active
                    ? "border-foreground ring-1 ring-foreground"
                    : "border-border/70 hover:border-primary",
              )}
            >
              <div className={cn(
                "grid size-9 shrink-0 place-items-center rounded-xl",
                active ? "bg-foreground/10" : "bg-muted/50",
              )}>
                <opt.icon className={cn("size-[18px]", active ? "text-foreground" : "text-muted-foreground")} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[15px] font-semibold text-foreground">{opt.title}</p>
                {opt.description && (
                  <p className="text-[13px] text-muted-foreground">{opt.description}</p>
                )}
                {opt.id === "company_credit" && companyCredit && companyCredit.sufficient && (
                  <>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t("booking.payCompanyChargeNote", { amount: fmtKm(companyCredit.chargeAmount) })}
                    </p>
                    {companyCredit.discountPercent > 0 && (
                      <p className="mt-0.5 text-[11px] text-muted-foreground/80">
                        {t("booking.payCompanyDiscountNote", { companyName: companyCredit.companyName, percent: companyCredit.discountPercent })}
                      </p>
                    )}
                  </>
                )}
                {opt.disabled && opt.disabledNote && (
                  <p className="mt-1 text-xs text-amber-700">{opt.disabledNote}</p>
                )}
              </div>
              {active && (
                <span className="grid size-5 shrink-0 place-items-center rounded-full bg-foreground">
                  <CheckIcon className="size-3 text-background" weight="bold" />
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
